import { useQueryClient } from "@tanstack/react-query";

import { UploadFileInput, UploadResponse } from "../../../api/uploads";
import { UseKlydoMutationOptions } from "../../../components/hooks";
import useKlydoMutation from "../../../components/hooks/useKlydoMutation";

const useUploadMutation = (
  uploadFn: (input: UploadFileInput) => Promise<UploadResponse>,
  mutationKey: string,
  options: Omit<
    UseKlydoMutationOptions<
      UploadResponse,
      UploadFileInput,
      { previousUploadStatus: UploadResponse | undefined }
    >,
    "onSuccess" | "onSettled"
  > & {
    onSuccess?: (response: UploadResponse) => void;
    onSettled?: (response: UploadResponse | undefined, error: unknown) => void;
  } = {},
) => {
  const queryClient = useQueryClient();

  return useKlydoMutation<
    UploadResponse,
    UploadFileInput,
    { previousUploadStatus: UploadResponse | undefined }
  >({
    mutationFn: uploadFn,
    mutationKey: [mutationKey],
    onMutate: async (input) => {
      await queryClient.cancelQueries({
        queryKey: [mutationKey],
      });

      const previousUploadStatus: UploadResponse | undefined =
        queryClient.getQueryData([mutationKey]);

      options.onMutate?.(input);

      return { previousUploadStatus };
    },
    onError: (error, input, context) => {
      if (context?.previousUploadStatus) {
        queryClient.setQueryData([mutationKey], context.previousUploadStatus);
      }
      options.onError?.(error, input, context);
    },
    onSettled: async (response, error) => {
      options.onSettled?.(response, error);
      await queryClient.invalidateQueries({
        queryKey: [mutationKey],
      });
    },
    onSuccess: (response) => {
      options.onSuccess?.(response);
    },
    ...options,
  });
};

export default useUploadMutation;
